<template>

  <ion-page id="lichesschallenge-page">
    <ion-content>

      <div v-if="showState == SHOW_EXPLAINTOUR">
        <ExplainTour :screen="explainTourScreen"
                      :wordsSpoken="explainTourWordsSpoken"
                      :wordsSpokenTrigger="explainTourWordsSpokenTrigger"
                    @toolbar-click="explainTourToolbarClick" />
      </div>

      <div v-if="showState == SHOW_MULTIPLE_OPTIONS">
        <MultiOptions :displayArray="multipleOptionsArray"
                        :wordsSpoken="multiOptionsWordsSpoken"
                        :wordsSpokenTrigger="multiOptionsWordsSpokenTrigger"
                        @item-selected="multipleOptionSelected"
                        @toolbar-click="multiOptionsToolbarClick" />
      </div>

      <div v-if="showState == SHOW_SINGLE_OPTION">
         <SingleOption :displayArray="singleOptionArray"
                        :descriptionHeading="singleOptionHeading"
                        :initialSelected="singleOptionSelected"
                        :wordsSpoken="singleOptionWordsSpoken"
                        :wordsSpokenTrigger="singleOptionWordsSpokenTrigger"

                        @item-selected="singleOptionClick" 
                        @toolbar-click="singleOptionToolbarClick"/>
      </div>

      <div v-show="showState == SHOW_CHALLENGE">
        <TopLine :style="styleTopline" :title="challengeTitle" :microphoneIsMuted="microphoneIsMuted" @topline-click="toplineClick"/>

        <div :style="styleMiddle" id="lichess-challenge">
          <ion-item v-show="isLoading">
            <ion-label class="ion-text-center">
              <h1>{{ translatePhrase("Loading Lichess Challenge Options") }}</h1>
            </ion-label>
          </ion-item>
          <ion-item>
            <ion-label class="ion-text-center">
              <h2>{{ parameterString1 }}</h2>
              <h2>{{ parameterString2 }}</h2>
              <h2>{{ parameterString3 }}</h2>
              <h2>{{ parameterString4 }}</h2>
              <p>&nbsp;</p>
            </ion-label>
          </ion-item>
          <ion-item>
            <ion-label class="ion-text-center  overline-label" color="danger">
              <h1>
            <b>{{ statusString }}</b>
            </h1>
            </ion-label>
          </ion-item>
          </div>
        <ToolBar :style="styleToolbar" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
      </div>
      </ion-content>
  </ion-page>
  

</template>

<script setup>

  import { IonPage, IonContent,IonList,IonItem,IonLabel  } from '@ionic/vue';
  import { buildStyleToolbar, buildStyleTopline,buildStyleMiddle,availableWidthHeight, scrollElement,
    calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'
  import { computed } from "@vue/reactivity"

  import { ref, watch, onBeforeUnmount } from 'vue'
  import ExplainTour from '@/components/ExplainTour.vue'
  import ToolBar from '@/components/ToolBar.vue'
  import TopLine from '@/components/TopLine.vue'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { useWindowSizeStore } from '@/stores/windowSizeStore';

  import AsyncLichessStream from '@/components/AsyncLichessStream.vue'

  import { doStartupCheck } from '@/composables/startupCheck.js'
  import { v4 as uuidv4 } from 'uuid';
  import { useGetData , getLichessData, generateErrorAlertText, Utf8ArrayToStr } from '../../composables/getData.js'
  import { buildWordListFromChoiceList , matchValue, parseGeneralWordsSaid } from '@/composables/wordlistSupport.js'
  import MultiOptions from '@/components/MultiOptions.vue'
  import SingleOption from '@/components/SingleOption.vue'
  import { translateEnglishWords, translatePhrase } from '@/composables/translation.js';

  const emit = defineEmits(['where-next','word-list','mic-is-muted'])

  const props = defineProps({
      wordsSaid : {
        type : String,
        default : ""
      },
      wordsSaidTrigger : {
        type : Number,
        default : 0
      }
    })

  const botVideo = ref(false)
  const wordsSaid = computed({get() {return props.wordsSaid}})
  const wordsSaidTrigger = computed({get() {return props.wordsSaidTrigger}})

  const connectionStore = useConnectionStore()
  const windowSizeStore = useWindowSizeStore()

  const singleOptionWordsSpokenTrigger = ref(0)
  const multiOptionsWordsSpokenTrigger = ref(0)
  
  const multiOptionsWordsSpoken = ref("")
  const singleOptionWordsSpoken = ref("")

  var isDoneWithLichessStream = false
  var exitWhenAborted = false
  var waitingToExitTimerCount = 0
  var exitDestination = "/lichessmenu"

  var hasActiveLichessStream = false
  var challengeType = connectionStore.getPlayType

  if (challengeType == "bot" && botVideo.value)
    challengeType = "bot_video"

  const lichessAccessToken = connectionStore.getLichessAccessToken

  const microphoneIsMuted = ref(connectionStore.getMicrophoneIsMuted)
  emit("mic-is-muted",microphoneIsMuted.value)

  var challengeIssued = false
  const isLoading = ref(true)

  const lichessURLEventStream = "https://lichess.org/api/stream/event"

  const parameterString1 = ref("")
  const parameterString2 = ref("")
  const parameterString3 = ref("")
  const parameterString4 = ref("")

  const statusString = ref("")

  var friendInfo = {}

  const challengeTitle = ref("Unknown")
  //console.log("CHALLENGE TYPE",challengeType)
  switch (challengeType) {
    case "rapid" :
      challengeTitle.value = "Rapid Challenge"
      break
    case "correspondence" :
      challengeTitle.value = "Correspondence Challenge"
      break
    case "bot_video" :
      challengeTitle.value = "' Rapid ' Challenge"
      break
    case "bot" :
      challengeTitle.value = "BOT Challenge"
      break
    case "friend" :
      friendInfo = connectionStore.getLichessFriend
      challengeTitle.value = "Challenge Friend: " + friendInfo.name
      break 

  }
  challengeTitle.value = translateEnglishWords(challengeTitle.value)

  const isDark = ref(connectionStore.getDarkmode != "light")
  
  const SHOW_CHALLENGE = 0
  const SHOW_EXPLAINTOUR = 1
  const SHOW_MULTIPLE_OPTIONS = 2
  const SHOW_SINGLE_OPTION = 3
  const showState = ref(SHOW_CHALLENGE)

  var singleOptionForChallenge = false
  var explainForState = SHOW_CHALLENGE

  const TOOLBAR_CHALLENGE = 0
  const TOOLBAR_OPTIONS = 1
  const TOOLBAR_EXPLAIN = 2

  const toolbarElements = ref([
    { index : TOOLBAR_CHALLENGE, label : translateEnglishWords("Challenge") ,disabled : false},
    { index : TOOLBAR_OPTIONS, label : translateEnglishWords("Options") ,disabled : false},
    { index : TOOLBAR_EXPLAIN, label : translateEnglishWords("Explain") ,disabled : false},
  ])

  const toolbarTrigger = ref(0)

  const styleToolbar = ref(buildStyleToolbar(availableWidthHeight()))
  const styleTopline = ref(buildStyleTopline(availableWidthHeight()))
  const styleMiddle = ref(buildStyleMiddle(availableWidthHeight()))

  const wordlistChallenge = "command challenge option options explain done mute listen"
  const wordlistExplain = "command done"
  const wordlistTrigger = ref(0)
  const wordlist = ref(wordlistChallenge)
  emit("word-list",wordlistChallenge)

  const explainTourWordsSpoken = ref("")
  const explainTourScreen = ref("")
  const explainTourWordsSpokenTrigger = ref(0)

  // OPTIONS

  const colorChoiceStrings = ["Random Color","Play as White", "Play as Black"]
  const colorChoiceWords = ["random","play as white", "play as black"]
  const colorChoiceValues = [ "r", "w" , "b" ]
  var colorCurrentValue = "r"
  var colorCurrentValueString = colorChoiceStrings[0]

  const ratedChoiceStrings = ["Yes","No"]
  const ratedChoiceWords = ["yes","no"]
  const ratedChoiceValues = [ 1, 0 ]
  var ratedCurrentValue = 1
  var ratedCurrentValueString = colorChoiceStrings[0]

  const playKeywordChoiceStrings =["Play Keyword Required","Piece Name Required - Including Pawn","No Play Keyword Required"]
  const playKeywordChoiceWords = ["play", "piece", "no"]
  const playKeywordChoiceValues = ["play", "piece", "no"]
  var playKeywordCurrentValue = "play"
  var playKeywordCurrentValueString = playKeywordChoiceStrings[0]
        
  const timeControlChoiceStrings = [
          "5 minutes , 5 sec increment",
					"10 minutes , no increment",
          "10 minutes , 5 sec increment",
					"15 minutes , 10 sec increment", 
          "30 minutes , no increment",
					"30 minutes , 20 sec increment", 
          "30 minutes , 30 sec increment",
					"45 minutes , 45 sec increment"]
  const timeControlChoiceWords = [
          "five minutes and five second sec increment",
					"ten minutes and no increment","ten minutes and five second sec increment",
					"fifteen minutes and ten second sec increment","thirty minutes and no increment",
					"thirty minutes and twenty second sec increment",
					"thirty minutes and thirty second sec increment",
					"forty five minutes and forty five second sec increment"]
  const timeControlChoiceValues = [ 505,1000,1005,1510,3000,3020,3030,4545 ]
  var timeControlCurrentValue = 505
  var timeControlCurrentValueString = colorChoiceStrings[0]

  const correspondenceChoiceStrings = ["1 day", "3 days", "5 days", "7 days", "14 days"]
  const correspondenceChoiceWords = ["one day","three days","five days","seven days","fourteen days"]
  const correspondenceChoiceValues = [ 1,3,5,7,14 ]
  var correspondenceCurrentValue = 1
  var correspondenceCurrentValueString = colorChoiceStrings[0]



  var optionDisplay = []
  var optionDisplayWords = []
  var optionValue = []
  var optionCurrent = []
  var optionValueStrings = []

  const rapidHeadings = ["Color You Play","Time Control", "Rated Game ?","Play Keyword Required ?"]
  const rapidHeadingWords = ["color you play","time control","rated game","play keyword"]
  const rapidVarnames = ["color", "time_control","rated","play_keyword"]

  const correspondenceHeadings = ["Color You Play","Time Control", "Rated Game ?","Play Keyword Required ?"]
  const correspondenceHeadingWords = ["color you play","time control","rated game","play keyword"]
  const correspondenceVarnames = ["color", "correspondence","rated","play_keyword"]

 
  var useOptionHeadings = []
  var useOptionHeadingWords = []
  var useOptionVarnames = []

  switch (challengeType) {
    case "rapid" :
    case "friend" :
    case "bot" :
    case "bot_video" :
      useOptionHeadings = rapidHeadings
      useOptionHeadingWords = rapidHeadingWords
      useOptionVarnames = rapidVarnames
      break
    case "correspondence" :
      useOptionHeadings = correspondenceHeadings
      useOptionHeadingWords = correspondenceHeadingWords
      useOptionVarnames = correspondenceVarnames
      break
  }

  const multipleOptionsArray = ref([])
  const singleOptionHeading = ref("")
  const singleOptionArray = ref([])
  var singleOptionDisplayWordsArray = []
  var useSingleOptionDisplayWords = ""
  var singleOptionServerField = ""
  const singleOptionSelected = ref("")

  var scrollCounter = 0
  const scrollTrigger = ref(0)
  const explainScrollTrigger = ref(0)
  const multiScrollTrigger = ref(0)

  var controller = null

  setupFetch(lichessURLEventStream,lichessAccessToken)

  watch(wordsSaidTrigger, (newVal,oldVal) => {
     //console.log('*********** words said ',props.wordsSaid)
     processWords(props.wordsSaid)
   })

  watch(() => windowSizeStore.getWindowSize, () => {
    styleToolbar.value = buildStyleToolbar(availableWidthHeight())
    styleTopline.value = buildStyleTopline(availableWidthHeight())
    styleMiddle.value = buildStyleMiddle(availableWidthHeight())
  })

  doStartupCheck(false,afterStartupCallbackRoutine)

  function afterStartupCallbackRoutine(stuffChanged) {
    getChallengeOptions()
  }

  // START ****************** MOVED FROM AsyncLichessStream

  function abortFunction(controller) {
    //console.log("******* IN Abort Function",controller)
    if (controller != null) {
      try {
        controller.abort()
      }
      catch (error) {
        console.log("abort error",error)
      }
    }
    controller = null
    //console.log("end abort function")
  }

  async function setupFetch (url,accessToken) {
    if (url == "" || accessToken == "")
      return

    //console.log("SETUP FETCH",url,accessToken)

    controller = new AbortController()

    var response = null
    try {
      response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept' : 'application/json',
          'Authorization' : "Bearer " + accessToken
        },
        signal : controller.signal
      })
    }
    catch(error) {
      console.error('Error:', error);
    }

    const reader = (response == null) ? null : response.body.getReader();

    // check if we're done with the stream on each loop. Calling program sets 'isDone'
    while (reader != null && controller != null && isDoneWithLichessStream == false) {
      const {value, done} = await reader.read();
      if (controller == null || done) 
        break;
      const receivedString = Utf8ArrayToStr(value)
      receivedEventString(receivedString)
      //console.log('Received', receivedString,value,isDoneWithLichessStream);
    }

    receivedEventString("ABORT")
    //console.log("before abort call")
    abortFunction(controller)
    //console.log('Response fully received');
   
  }

  // END ****************** MOVED FROM AsyncLichessStream

  function receivedEventString(string) {
    //console.log("***** GOT EVENT STRING ->",challengeIssued,string)

    // The event stream has to finish before we leave the screen
    if (string == "ABORT") {
      hasActiveLichessStream = false
      // timer will send them to calling View
      return
    }

    hasActiveLichessStream = true

    // getting all the stream traffic so we know it is alive
    if (string == "\n")
      return

    // ignore any strings until "Challenge" issued.  (can be list of already open games)
    if (challengeIssued == false)
      return

    const events = string.split("\n");
    //console.log("***** Events",events)
    if (events.length == 0)
      return;
    for (var i=0; i < events.length; i++) {
      if (events[i].length == 0)
        continue
      const oneEvent = JSON.parse(events[i])
      //console.log("*** event ",i,oneEvent)
      if (oneEvent.hasOwnProperty('type')) {
        switch (oneEvent['type']) {
          case "gameStart" :
            statusString.value = translateEnglishWords("Challenge Accepted !")
            const oneGame = oneEvent['game']
            const gameId = oneGame['gameId']
            //console.log("*** one game",oneGame)
            //console.log("**** game ID",gameId)
            playGame(gameId)
            break
          case "gameFinish" :
            break
          case "challenge" :
            break
          case "challengeCanceled" :
            break
          case "challengeDeclined" :
            statusString.value = translateEnglishWords("Challenge Declined")
            break
        }
      }
  
    }
  }

  onBeforeUnmount(() => {
    //console.log("**************** Lichess Challenge unmounted")
    
    isDoneWithLichessStream = true

  })

 function toplineClick(whatClicked) {
    //console.log("topline",whatClicked)
    switch (whatClicked) {
      case "done" :
        processWords("command done")
        break;
      case "microphone" :
        microphoneIsMuted.value = !microphoneIsMuted.value 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
        break

    }
  }

  // START Explain Tour Routines

  function explainTourInitialize(pageName,errorText) {
    //console.log("in explain tour initialize")
    showState.value = SHOW_EXPLAINTOUR
    explainTourScreen.value = (errorText == null) ? pageName : ("ERROR:" + errorText);
    wordlist.value = connectionStore.getExplainTourWords
    emit("word-list",wordlist.value)

    wordlistTrigger.value++
  }

  function explainTourToolbarClick(whatClicked) {
    switch (whatClicked) {
      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "done" :
        showState.value = SHOW_CHALLENGE
        wordlist.value = wordlistChallenge
        emit("word-list",wordlist.value)
        wordlistTrigger.value++
        break
    }
  }

  // END Explain Tour Routines

    function multiOptionsToolbarClick(selected) {
    //console.log("multioption toolbar click",selected)
    toolbarTrigger.value++
    switch (selected) {
      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "explain" :
        explainTourInitialize("options_multiple",null)
        break
      case "done" :
        switchToChallenge()
        break
    }
  }

  function singleOptionToolbarClick(selected) {
    //console.log("single option toolbar click",selected)
    toolbarTrigger.value++
    switch (selected) {
      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "explain" :
        explainTourInitialize("options_single",null)
        break
      case "done" :
        buildMultipleOptionsDisplay()
        break
    }
  }

  function toolbarClick(arrayIndex) {
    //console.log("toolbar click and show state ",arrayIndex,showState.value)
    switch (arrayIndex) {
      case TOOLBAR_CHALLENGE :
        processWords("command challenge")
        break
      case TOOLBAR_OPTIONS :
        processWords("command options")
        break
      case TOOLBAR_EXPLAIN :
        processWords("command explain")
        break
    }
  }

  function switchToChallenge() {
    showState.value = SHOW_CHALLENGE
    wordlist.value = wordlistChallenge
    emit("word-list",wordlist.value)
    wordlistTrigger.value++
    toolbarTrigger.value++
  }

  function multipleOptionSelected(selected) {
    //console.log("**** MULTIPLE Option Selected",selected,useOptionHeadingWords[selected])

    //console.log("**** DO SINGLE FOR CHOICE -> ",selected)
    const oneOption = multipleOptionsArray.value[selected]
    //console.log("ONE OPTION",oneOption)
    singleOptionHeading.value = oneOption.heading
    singleOptionArray.value = []
    singleOptionDisplayWordsArray = []
    for (var i=0; i < oneOption.displayWords.length; i++) {
      singleOptionDisplayWordsArray.push(oneOption.displayWords[i])
      const oneDisplay = {
            index : i,
            name : translateEnglishWords(oneOption.display[i]),
            id : oneOption.values[i],
            words : oneOption.displayWords[i],
            radio : "" + oneOption.values[i]
      }
      singleOptionArray.value.push(oneDisplay)
    }
    singleOptionSelected.value = "" + oneOption.current
    singleOptionServerField = oneOption.varname
    useSingleOptionDisplayWords = buildWordListFromChoiceList(singleOptionDisplayWordsArray)
    //console.log("****************** single option array",singleOptionArray.value)
    //console.log("****************** single option word list",useSingleOptionDisplayWords)

    wordlist.value = useSingleOptionDisplayWords
    emit("word-list",wordlist.value)
    wordlistTrigger.value++

    showState.value = SHOW_SINGLE_OPTION
    //processWordsForMultipleOptions("command " + useOptionHeadingWords[selected])
  }

  function singleOptionClick(index) {
    //console.log("**** SINGLE Option Selected",index)

    const databaseValue = singleOptionArray.value[index].radio
    //console.log("**** SINGLE VALUE SELECTED -> ",databaseValue)

    singleOptionSelected.value = singleOptionArray.value[index].radio
    //console.log("single server field",singleOptionServerField)
    setOption(singleOptionServerField,databaseValue)
  }

  // function singleOptionClick(selected) {
  //   //console.log("**** SINGLE Option Selected",selected,singleOptionDisplayWordsArray[selected])
  //   processWordsForSingleOption("command " + singleOptionDisplayWordsArray[selected])
  // }

  function setupMultipleOptions() {
    multipleOptionsArray.value = []
    for (var i = 0; i < optionDisplay.length; i++) {
      const oneOption = {
          index : i,
          heading : useOptionHeadings[i],
          headingWords : useOptionHeadingWords[i],
          varname : useOptionVarnames[i],
          display : optionDisplay[i],
          displayWords : optionDisplayWords[i],
          values : optionValue[i],
          current : optionCurrent[i],
          currentString : optionValueStrings[i]
      }
      multipleOptionsArray.value.push(oneOption)
    }
    //console.log("options",multipleOptionsArray.value)

    showState.value = SHOW_MULTIPLE_OPTIONS
    
    wordlist.value = buildWordListFromChoiceList(useOptionHeadingWords)
    emit("word-list",wordlist.value)
    wordlistTrigger.value++
  }

  function parseCommands(words,currentWordIndex) {
    
    //console.log("in parse commands",words,currentWordIndex)
    var currentWord = (words.length > currentWordIndex) ? words[currentWordIndex] : "xyz"
    var nextWord = (words.length > currentWordIndex+1) ? words[currentWordIndex+1] : "xyz"
    var nextNextWord = (words.length > currentWordIndex+2) ? words[currentWordIndex+2] : "xyz"

    for (var i = 0; i < words.length; i++) {
        if (currentWord == "ignore") {
            return ["ignore",""];
        }
        if (currentWord == "explain") {
            return ["explain",""]
        }
        if (currentWord == "listen") {
            return ["listen",""]
        }
        if (currentWord == "mute") {
            return ["mute",""]
        }
        if (currentWord == "done") {
            return ["done",""]
        }
        if (currentWord == "scroll") {
            if (nextWord == "up") {
                return ["scroll", "up"]
            }
            if (nextWord == "down") {
                return ["scroll", "down"]
            }
        }
        
        // add choices here
      
        if (currentWord == "options") {
          return (["options", ""])
        }
        if (currentWord == "challenge") {
            return ["challenge",""]
        }

        currentWord = nextWord;
        nextWord = nextNextWord;
        nextNextWord = (i < words.length - 3) ? words[i + 3] : "xyz";
    }
    return (["ignore",""]);
  }

  function parseWordsSaid(str) {
  
    const words = str.split(" ")

    //console.log("string ",str)
    //console.log("words",words,words.length)
    if (words.length == 0) {
        return ["ignore",""];
    }

    var currentWord = (words.length > 0) ? words[0] : "xyz"
    var nextWord = (words.length > 1) ? words[1] : "xyz"
    var nextNextWord = (words.length > 2) ? words[2] : "xyz"
          
    //console.log("current word",currentWord)

    for (var i = 0; i < words.length; i++) {
        if (currentWord == "ignore") {
            return ["ignore",""];
        }

        if (currentWord == "command") {
          let returnArray = parseCommands(words, i+1)
            return returnArray
        }
        currentWord = nextWord;
        nextWord = nextNextWord;
        nextNextWord = (i < words.length - 3) ? words[i + 3] : "xyz";
    }
    return ["ignore",""]
  }

  function processWordsForChallenge(text) {
    //console.log("***** words for challenge",text)
    const returnArray = parseWordsSaid(text)
    //console.log("command found",returnArray)

    switch (returnArray[0]) {
          case "scroll" :
            scrollElement("lichess-challenge",returnArray[1])
            break
          case "options" :
            //console.log("options",showState.value)
            if (showState.value == SHOW_CHALLENGE)
              buildMultipleOptionsDisplay()
            break
          case "explain" :
            explainTourInitialize("lichess_challenge",null)
            break;
          case "mute" :
            microphoneIsMuted.value = true 
            emit("mic-is-muted",microphoneIsMuted.value)
            connectionStore.setMicrophoneIsMuted(true)
            break
          case "listen" :
            microphoneIsMuted.value = false 
            emit("mic-is-muted",microphoneIsMuted.value)
            connectionStore.setMicrophoneIsMuted(false)
            break
          case "done" :
            emit("where-next","/lichessmenu")

            /*
            // if there isn't an active stream, we can leave. Other flag to end stream
            if (!hasActiveLichessStream)
              emit("where-next","/lichessmenu")
            else {
              isDoneWithLichessStream = true
              exitWhenAborted = true;
              waitingToExitTimer()
            }
            //router.replace({ path: "/lichessmenu"})
            */
            break;
          case "challenge" :
            toolbarElements.value[TOOLBAR_CHALLENGE].disabled = true
            toolbarElements.value[TOOLBAR_OPTIONS].disabled = true
            statusString.value = translateEnglishWords("*** Challenge Issued ***")
            switch (challengeType) {
              case "rapid" :
                sendRapidChallenge()
                break
              case "friend" :
                sendFriendChallenge()
                break
              case "correspondence" :
                sendCorrespondenceChallenge()
                break
              case "bot" :
              case "bot_video" :
                sendBotChallenge()
                break
            }
            break;
    }
  }

  function processWords(text) {
       switch (showState.value) {
      case SHOW_EXPLAINTOUR :
        //console.log("*** words for explain tour",text)
        explainTourWordsSpoken.value = text
        explainTourWordsSpokenTrigger.value++
        break
      case SHOW_MULTIPLE_OPTIONS :
        multiOptionsWordsSpoken.value = text
        multiOptionsWordsSpokenTrigger.value++
        break
      case SHOW_SINGLE_OPTION :
        singleOptionWordsSpoken.value = text
        singleOptionWordsSpokenTrigger.value++
        break
      case SHOW_CHALLENGE :
        processWordsForChallenge(text)
        break
    }
  }

  function displayStatus() {
    parameterString1.value = colorCurrentValueString
    parameterString3.value = translateEnglishWords("Rated") + ": " + ratedCurrentValueString
    parameterString4.value = playKeywordCurrentValueString
    parameterString2.value = (challengeType == "correspondence") ? 
                correspondenceCurrentValueString : timeControlCurrentValueString
    statusString.value = translateEnglishWords("Issue Challenge ?")
  }
    

  function buildMultipleOptionsDisplay() {
    if (challengeType == "correspondence") {
      optionDisplay = [ colorChoiceStrings,correspondenceChoiceStrings,ratedChoiceStrings,playKeywordChoiceStrings]
      optionDisplayWords = [ colorChoiceWords,correspondenceChoiceWords,ratedChoiceWords,playKeywordChoiceWords]
      optionValue = [ colorChoiceValues,correspondenceChoiceValues,ratedChoiceValues,playKeywordChoiceValues]
      optionCurrent = [colorCurrentValue,correspondenceCurrentValue,ratedCurrentValue,playKeywordCurrentValue]
      optionValueStrings = [colorCurrentValueString,correspondenceCurrentValueString,ratedCurrentValueString,playKeywordCurrentValueString]
    }
    else {
      optionDisplay = [ colorChoiceStrings,timeControlChoiceStrings,ratedChoiceStrings,playKeywordChoiceStrings]
      optionDisplayWords = [ colorChoiceWords,timeControlChoiceWords,ratedChoiceWords,playKeywordChoiceWords]
      optionValue = [ colorChoiceValues,timeControlChoiceValues,ratedChoiceValues,playKeywordChoiceValues]
      optionCurrent = [colorCurrentValue,timeControlCurrentValue,ratedCurrentValue,playKeywordCurrentValue]
      optionValueStrings = [colorCurrentValueString,timeControlCurrentValueString,ratedCurrentValueString,playKeywordCurrentValueString]
    }
    
    multipleOptionsArray.value = []
    for (var i = 0; i < optionDisplay.length; i++) {
      const oneOption = {
          index : i,
          heading : translateEnglishWords(useOptionHeadings[i]),
          headingWords : useOptionHeadingWords[i],
          varname : useOptionVarnames[i],
          display : optionDisplay[i],
          displayWords : optionDisplayWords[i],
          values : optionValue[i],
          current : optionCurrent[i],
          currentString : optionValueStrings[i]
      }
      multipleOptionsArray.value.push(oneOption)
    }
    //console.log("options",multipleOptionsArray.value)

    wordlist.value = buildWordListFromChoiceList(useOptionHeadingWords)
    emit("word-list",wordlist.value)
    wordlistTrigger.value++
    showState.value = SHOW_MULTIPLE_OPTIONS    
  }

  function getArrayIndexFromValueMatch(choiceValues,current) {
    for (var i=0; i < choiceValues.length; i++)
      if (current == choiceValues[i])
        return (i)
    return (0)
  }

  function processOptionsJsonResponse (json) {
    //console.log(" in lichess options callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(null,generateErrorAlertText(json))
      return;
    }

    colorCurrentValue = json["color"]
    ratedCurrentValue = json["rated"]
    //console.log("from database rated and color",ratedCurrentValue,colorCurrentValue)
    timeControlCurrentValue = json["time_control"]
    correspondenceCurrentValue = json["correspondence"]
    playKeywordCurrentValue = json["play_keyword"]

    const colorIndex = getArrayIndexFromValueMatch(colorChoiceValues,colorCurrentValue)
    const ratedIndex = getArrayIndexFromValueMatch(ratedChoiceValues,ratedCurrentValue)
    const timeControlIndex = getArrayIndexFromValueMatch(timeControlChoiceValues,timeControlCurrentValue)
    const correspondenceIndex = getArrayIndexFromValueMatch(correspondenceChoiceValues,correspondenceCurrentValue)
    const playKeywordIndex = getArrayIndexFromValueMatch(playKeywordChoiceValues,playKeywordCurrentValue)

    colorCurrentValueString = translateEnglishWords(colorChoiceStrings[colorIndex])
    ratedCurrentValueString = translateEnglishWords(ratedChoiceStrings[ratedIndex])
    timeControlCurrentValueString = translateEnglishWords(timeControlChoiceStrings[timeControlIndex])
    correspondenceCurrentValueString = translateEnglishWords(correspondenceChoiceStrings[correspondenceIndex])
    playKeywordCurrentValueString = translatePhrase(playKeywordChoiceStrings[playKeywordIndex])

    displayStatus()
    isLoading.value = false
  }
  

  function getChallengeOptions() {
    var formData = {
    }

    useGetData("getweb2_verbal_lichess_options.php", formData, processOptionsJsonResponse)
  }

  function processSetOptionJsonResponse (json) {
    //console.log(" in set option callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(null,generateErrorAlertText(json))
      return;
    }
    getChallengeOptions()
  }

  function setOption(fieldName,value) {
    var formData = {
      name : fieldName,
      value : value
    }

    //console.log("Set Option ",formData)
    useGetData("set17_verbal_lichess_options_ios.php", formData,processSetOptionJsonResponse)
  }

  function processRapidGameJsonResponse (json) {
    //console.log(" in rapid game callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(null,generateErrorAlertText(json))
      return;
    }

    if (json.hasOwnProperty("gameid")) {
      toolbarElements.value[TOOLBAR_CHALLENGE].disabled = false
      toolbarElements.value[TOOLBAR_OPTIONS].disabled = false
      toolbarTrigger.value++
      statusString.value = translateEnglishWords("Game Created")
      const gameId = json['gameid']
      //console.log("game created! ",gameId)
      playGame(gameId)
    }
    else {
      toolbarElements.value[TOOLBAR_CHALLENGE].disabled = false
      toolbarElements.value[TOOLBAR_OPTIONS].disabled = false
      toolbarTrigger.value++
      statusString.value = translateEnglishWords("Game Not Created")
    }
  }

  function sendBotChallenge() {
          
    const ratedString = (ratedCurrentValue == 1) ? "true" : "false"
    var colorString = "random"
    var botName = "ai"
    var botLevel = "1"
    var postData = ""
    var urlStr = "https://lichess.org/api/challenge/"
    
    if (colorCurrentValue == "w")
      colorString = "white"
    else
    if (colorCurrentValue == "b")
      colorString = "black"
    const timeControl = parseInt(timeControlCurrentValue)
    const minutes = Math.floor(timeControl / 100)
    const seconds = minutes * 60
    const increment = timeControl % 100

    var formData = {
      level : botLevel,
      color : colorString,
      rated : ratedString,
      "clock.limit" : seconds,
      "clock.increment" : increment
    }
    urlStr += botName;
        
    //console.log("URL",urlStr)
    //console.log(" bot formdata",formData)
    //console.log("accesstoken",lichessAccessToken)

    challengeIssued = true

    getLichessData(urlStr, lichessAccessToken,formData, processLichessBotChallengeResponse) 
  }

  function processLichessBotChallengeResponse(json) {
    //console.log("**** JSON from BOT",json)
    toolbarElements.value[TOOLBAR_CHALLENGE].disabled = false
    toolbarElements.value[TOOLBAR_OPTIONS].disabled = false
    toolbarTrigger.value++

    if (json.hasOwnProperty('id')) {
      const gameId = json['id']
      playGame(gameId)
    }
  }

  function processFriendChallengeResponse(json) {
    //console.log("************   Friend Challenge Response",json)
    toolbarElements.value[TOOLBAR_CHALLENGE].disabled = false
    toolbarElements.value[TOOLBAR_OPTIONS].disabled = false
    toolbarTrigger.value++
  }

  function processRapidChallengeResponse(json) {
    //console.log("************   Rapid Challenge Response",json)
    toolbarElements.value[TOOLBAR_CHALLENGE].disabled = false
    toolbarElements.value[TOOLBAR_OPTIONS].disabled = false
    toolbarTrigger.value++
    statusString.value = translateEnglishWords("*** Send Challenge Again ? ***")
  }

  function startFriendChallengeTimer() {
    const timerSleepAmount = 21000

    function oneInterval() {
      clearInterval(id)
      toolbarElements.value[TOOLBAR_CHALLENGE].disabled = false
      toolbarElements.value[TOOLBAR_OPTIONS].disabled = false
      toolbarTrigger.value++
      statusString.value = translateEnglishWords("*** Challenge Expired .  Send Again ? ***")
    }
    var id = setInterval(oneInterval, timerSleepAmount)
  }

  // send Friend challenges straight to lichess.org
  function sendFriendChallenge() {
          
    const ratedString = (ratedCurrentValue == 1) ? "true" : "false"
    var colorString = "random"
    var postData = ""
    var urlStr = "https://lichess.org/api/challenge/" + friendInfo.id
    
    if (colorCurrentValue == "w")
      colorString = "white"
    else
    if (colorCurrentValue == "b")
      colorString = "black"
      
    const minutes = Math.floor(timeControlCurrentValue / 100)
    const seconds = minutes * 60
    const increment = timeControlCurrentValue % 100

    var formData = {
      color : colorString,
      rated : ratedString,
      "clock.limit" : seconds,
      "clock.increment" : increment
    }
        
    //console.log("URL",urlStr)
    //console.log("formdata",formData)
    //console.log("accesstoken",lichessAccessToken)

    challengeIssued = true
    startFriendChallengeTimer()
    getLichessData(urlStr, lichessAccessToken,formData, processFriendChallengeResponse) 
  }

  function sendRapidChallenge() {
          
    const ratedString = (ratedCurrentValue == 1) ? "true" : "false"
    var colorString = "random"
    var postData = ""
    var urlStr = "https://lichess.org/api/board/seek"
    
    if (colorCurrentValue == "w")
      colorString = "white"
    else
    if (colorCurrentValue == "b")
      colorString = "black"
      
    const minutes = Math.floor(timeControlCurrentValue / 100)
    const seconds = minutes * 60
    const increment = timeControlCurrentValue % 100

    var formData = {
      color : colorString,
      rated : ratedString,
      "time" : minutes,
      "increment" : increment
    }
        
    //console.log("URL",urlStr)
    //console.log("accesstoken",lichessAccessToken)
    //console.log("formData",formData)

    challengeIssued = true
    getLichessData(urlStr, lichessAccessToken,formData, processRapidChallengeResponse) 
  }

  function playGame(gameId) {
    connectionStore.setLichessGameId(gameId + ",/lichesschallenge")
    isDoneWithLichessStream = true
    // if there isn't an active stream, we can leave. Other flag to end stream
    emit("where-next","/lichessplay")

    // if (!hasActiveLichessStream)
    //   emit("where-next","/lichessplay")
    // else {
    //   exitDestination = "/lichessplay"
    //   isDoneWithLichessStream = true
    //   exitWhenAborted = true;
    //   waitingToExitTimer()
    // }
    //router.replace("/lichessplay")
    // router push
  }

  // this comes back from lichess.org
  function processLichessCorrespondenceChallengeResponse(json) {
    //console.log(" in lichess correspondence challenge callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(null,generateErrorAlertText(json))
      return;
    }

    statusString.value = translatePhrase("When Challenge Accepted, it will be in your 'Open Games' list")
    toolbarElements.value[TOOLBAR_CHALLENGE].disabled = false
    toolbarElements.value[TOOLBAR_OPTIONS].disabled = false
    toolbarTrigger.value++
  }

  // send correspondence challenges straight to lichess.org
  function sendCorrespondenceChallenge() {
          
    const ratedString = (ratedCurrentValue == 1) ? "true" : "false"
    var colorString = "random"
    var postData = ""
    var urlStr = "https://lichess.org/api/board/seek"
    
    if (colorCurrentValue == "w")
        colorString = "white"
    else
    if (colorCurrentValue == "b")
        colorString = "black"

    const days = parseInt(correspondenceCurrentValue)

    var formData = {
      color : colorString,
      rated : ratedString,
      days : days
    }  

    //console.log("URL",urlStr)
    //console.log("accesstoken",lichessAccessToken)
    //console.log("formdata",formData)

    challengeIssued = true
    getLichessData(urlStr, lichessAccessToken,formData, processLichessCorrespondenceChallengeResponse) 
  }

  function processFriendGameJsonResponse (json) {
    //console.log(" in friend game callback",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(null,generateErrorAlertText(json))
      return;
    }

    toolbarElements.value[TOOLBAR_CHALLENGE].disabled = false
    toolbarElements.value[TOOLBAR_OPTIONS].disabled = false
    toolbarTrigger.value++

    if (json.hasOwnProperty("gameid")) {
      const gameId = json['gameid']
      if (gameId == "DECLINE")
        statusString.value = translatePhrase("Challenge Declined")
      else {
        statusString.value = translatePhrase("Challenge Accepted")
        //console.log("game created! ",gameId)
        playGame(gameId)
      }
    }
    else
      statusString.value = translateEnglishWords("No Response")
  }

</script>

<style scoped>

.overline-label {
  text-decoration-line: underline; /* Applies an overline to the text */
  text-decoration-color: red; /* Sets the color of the overline */
  text-decoration-thickness: 3px; /* Sets the thickness of the overline */
}

</style>